<template>
  <div class="flex flex-col gap-y-1" ref="wrapperDomRef">
    <label class="w-full flex flex-col gap-0.5">
      <div
        :class="direction"
        class="w-full flex gap-x-4 gap-y-1 text-brand-black-secondary flex-wrap"
      >
        <div v-if="label" :class="labelClass" class="flex md:w-1/2 items-center text-xs md:text-base">
          {{ label }} <span v-if="isRequired" class="text-brand-red">*</span>
        </div>
        <div class="w-full flex flex-col gap-y-2">
          <slot name="topAppendage"></slot>
          <VDatePicker :min-date="minDateRef" :max-date="maxDateRef" v-model="dateValue">
            <template #default="{ togglePopover }">
              <button
                :class="[
                  inputClass,
                  disabled ? 'cursor-not-allowed bg-brand-gray-250' : 'cursor-pointer'
                ]"
                :disabled="disabled"
                class="w-full relative px-3 py-1.5 flex justify-between items-center border-s-4 border-s-primary rounded-sm border border-brand-grayblue-300 outline-none focus:border-primary placeholder:text-xs text-xs text-start"
                @click.prevent.stop="togglePopover"
              >
                <span v-if="!field.value" class="text-brand-gray-600 text-xs">
                  {{ t9nPlaceholder || t9nLabel }}
                </span>
                <span v-else> {{ formattedDate }} </span>
                <div class="flex gap-x-3">
                  <button v-if="field.value" @click.stop.prevent="$emit('onChange', null)">
                    <faTimes class="w-3 fill-brand-red" />
                  </button>
                  <faCalendar class="w-4 fill-brand-black-body" />
                </div>
              </button>
            </template>
          </VDatePicker>
          <slot name="bottomAppendage"></slot>
        </div>
      </div>
    </label>

    <div class="flex flex-col gap-y-1 text-xs ltr:font-sans">
      <span :key="err" v-for="err in errors" class="text-brand-red">
        {{ $t(err) }}
      </span>
    </div>
  </div>
</template>

<script setup>
import faCalendar from '@/assets/icons/faCalendar.svg?component'
import faTimes from '@/assets/icons/faTimes.svg?component'
import useFormatDate from '@/composables/useFormatDate'
import { useDebounceFn, useEventListener } from '@vueuse/core'
import { DateTime } from 'luxon'
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

const dateValue = ref()
const formattedDate = ref()

const i18n = useI18n()

const props = defineProps({
  label: String,
  field: Object,
  errors: Array,
  name: String,
  mask: String,
  placeholder: String,
  minDate: String,
  maxDate: [String, Date],
  defaultValue: [String, Date],
  serverDateFormat: {
    type: String,
    default: () => 'dd/MM/yyyy'
  },
  inputClass: {
    type: String,
    default: () => ''
  },
  fuzzyKeywords: {
    type: Array,
    default: () => ['label']
  },
  labelClass: String,
  disabled: {
    type: Boolean,
    default: null
  },
  direction: {
    type: String,
    default: 'flex-row md:items-center'
  },
  isRequired: {
    type: Boolean,
    default: () => true
  }
})

const emit = defineEmits('onChange')

const { formateDateFromDDMMYYYY } = useFormatDate()

const t9nLabel = computed(() => i18n.t(props.label))
const t9nPlaceholder = computed(() => props.placeholder && i18n.t(props.placeholder))

const searchValue = ref()
const wrapperWidth = ref()
const wrapperDomRef = ref()
const fuse = ref()

const maxDateRef = computed(() => {
  if (props.maxDate) {
    if (props.maxDate == '__now__') {
      return DateTime.now().toFormat(props.serverDateFormat)
    } else {
      return formateDateFromDDMMYYYY(props.maxDate).toJSDate()
    }
  }
})

const minDateRef = computed(() => {
  if (props.minDate) {
    if (props.minDate == '__now__') {
      return DateTime.now().toFormat(props.serverDateFormat)
    } else {
      return formateDateFromDDMMYYYY(props.minDate).toJSDate()
    }
  }
})

onMounted(() => {
  const defaultValueSources = props.defaultValue || props.field.value
  if (defaultValueSources) {
    dateValue.value = DateTime.fromFormat(defaultValueSources, props.serverDateFormat).toJSDate()
    formattedDate.value = defaultValueSources
  }
})

const displayLabel = computed(() => {
  if (!props.field.value) return
  return filteredOptions.value.find((fo) => fo.code === props.field.value)
})

onBeforeUnmount(() => {
  cleanupResize()
})

const cleanupResize = useEventListener(
  window,
  'resize',
  useDebounceFn((e) => {
    setTippyWidth()
  }, 1000)
)

const filteredOptions = computed(() => {
  if (!searchValue.value) return props.options
  const result = fuse.value.search(searchValue.value)

  return result.map((r) => r.item)
})

const setTippyWidth = () => {
  wrapperWidth.value = wrapperDomRef.value.offsetWidth + 'px'
}

watch(dateValue, (newDate) => {
  formattedDate.value = DateTime.fromJSDate(newDate).toFormat(props.serverDateFormat)
  emit('onChange', DateTime.fromJSDate(newDate).toFormat(props.serverDateFormat))
})
</script>

<style lang="scss" scoped></style>
